import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, StyleSheet, Pressable, ActivityIndicator } from 'react-native';
import { router, usePathname, type Href } from 'expo-router';
import { useTheme, SPACING, TYPOGRAPHY } from '../../theme';
import { useUIStore } from '../../store/uiStore';
import { useAuthStore } from '../../store/authStore';
import { useStructuresStore } from '../../store/structuresStore';
import { Structure, StructureClaim } from '../../types';
import { PropertyListRow } from './PropertyListRow';

interface PickerRow {
  structure: Structure;
  claim: StructureClaim;
  roleLabel: 'Owner' | 'Asked to evaluate';
  href: Href;
}

const EMPTY_CLAIM: StructureClaim = {} as StructureClaim;

export interface PropertyPickerBodyProps {
  /** Whether this view is the one currently showing — drives the
   *  fetch-on-open effect below. Same "visible prop or fresh mount either
   *  way" reasoning as AddPropertyForm/PlanMapImportForm. */
  visible: boolean;
  onClose: () => void;
  /** The desktop dock (PropertyDockPanel.web.tsx) shows this body's title
   *  in its own pinned header instead (panel-system.md review, note 11) —
   *  set there so the body doesn't repeat it. */
  hideTitle?: boolean;
}

/**
 * The body half of the property-picker surface (panel-system.md Phase 2,
 * same split as DefensePlanAssignForm/Sheet): "System lists 0-N properties,
 * and next to property says my role (owner, 3rd party evaluator)" (client
 * ask, Sept 2026). Shown instead of jumping straight to a structure whenever
 * the Property tab (useOpenProperty.resolvePropertyTarget) can't tell which
 * property the user means. PropertyPickerSheet hosts this in an
 * AdaptiveSheet everywhere except the desktop map route, which docks it
 * directly instead (PropertyDockPanel.web.tsx).
 */
export function PropertyPickerBody({
  visible,
  onClose,
  hideTitle = false,
}: PropertyPickerBodyProps) {
  const { colors } = useTheme();
  const pendingProperty = useUIStore((s) => s.pendingProperty);
  const requestMapSearchFocus = useUIStore((s) => s.requestMapSearchFocus);
  const user = useAuthStore((s) => s.user);
  const pathname = usePathname();
  const fetchClaimedStructures = useStructuresStore((s) => s.fetchClaimedStructures);
  const fetchStructuresByIds = useStructuresStore((s) => s.fetchStructuresByIds);

  const [rows, setRows] = useState<PickerRow[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch on open: the user's claimed structures (claimedStructureIds may
  // not be warm yet — resolvePropertyTarget already fell back to whatever
  // the live map knew to decide "picker" at all, but this sheet wants real
  // addresses, not just ids) plus the pending-link structure, if any.
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      if (user) await fetchClaimedStructures(user.id);
      if (cancelled) return;
      const { claims: freshClaims, claimedStructureIds: freshIds } = useStructuresStore.getState();
      const idsToFetch = Array.from(
        new Set([...freshIds, ...(pendingProperty ? [pendingProperty.structureId] : [])])
      );
      const structures = await fetchStructuresByIds(idsToFetch);
      if (cancelled) return;
      const byId = new Map(structures.map((s) => [s.id, s]));

      const nextRows: PickerRow[] = [];
      // Skip the pending row entirely if it turns out to be a property the
      // user already owns (an insurer link pointing at their own address) —
      // the Owner row below covers it, and showing the same address twice
      // with two different role labels would be confusing, not helpful.
      if (pendingProperty && !freshIds.includes(pendingProperty.structureId)) {
        const structure = byId.get(pendingProperty.structureId);
        if (structure) {
          nextRows.push({
            structure,
            claim: freshClaims[structure.id] ?? EMPTY_CLAIM,
            roleLabel: 'Asked to evaluate',
            href: (pendingProperty.checklistId
              ? `/structure/${structure.id}?model=${encodeURIComponent(pendingProperty.checklistId)}`
              : `/structure/${structure.id}`) as Href,
          });
        }
      }
      for (const id of freshIds) {
        const structure = byId.get(id);
        const claim = freshClaims[id];
        if (!structure || !claim) continue;
        nextRows.push({ structure, claim, roleLabel: 'Owner', href: `/structure/${id}` as Href });
      }
      setRows(nextRows);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, user, pendingProperty, fetchClaimedStructures, fetchStructuresByIds]);

  const handleRowPress = useCallback(
    (href: Href) => {
      onClose();
      router.push(href);
    },
    [onClose]
  );

  const handleFindAnother = useCallback(() => {
    onClose();
    // Same as useOpenProperty's search fallback: push to tabs first if
    // we're not there, then ask the map to focus its address bar.
    if (pathname !== '/') router.push('/(tabs)' as Href);
    requestMapSearchFocus();
  }, [onClose, pathname, requestMapSearchFocus]);

  return (
    <View testID="property-picker-sheet" style={styles.content}>
      {!hideTitle && <Text style={[styles.title, { color: colors.text }]}>Your properties</Text>}

      {loading && rows.length === 0 && (
        <ActivityIndicator size="small" color={colors.textSecondary} style={styles.loading} />
      )}

      {rows.map((row) => (
        <PropertyListRow
          key={row.structure.id}
          structure={row.structure}
          claim={row.claim}
          roleLabel={row.roleLabel}
          onPress={() => handleRowPress(row.href)}
          testID={`property-picker-row-${row.structure.id}`}
        />
      ))}

      <Pressable
        onPress={handleFindAnother}
        accessibilityRole="button"
        accessibilityLabel="Find another property"
        style={[styles.findAnother, { borderTopColor: colors.divider }]}
        testID="property-picker-search">
        <Text style={[styles.findAnotherText, { color: colors.text }]}>Find another property</Text>
      </Pressable>

      {/* Scroll sentinel: Maestro flows assert this is reachable. */}
      <View testID="property-picker-end" style={styles.scrollSentinel} />
    </View>
  );
}

const styles = StyleSheet.create({
  content: {
    padding: SPACING.lg,
    paddingBottom: SPACING.xxl,
  },
  scrollSentinel: {
    height: 1,
  },
  title: {
    ...TYPOGRAPHY.h4,
    marginBottom: SPACING.md,
  },
  loading: {
    marginVertical: SPACING.lg,
  },
  findAnother: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: SPACING.smd,
    marginTop: SPACING.md,
    borderTopWidth: 1,
  },
  findAnotherText: {
    ...TYPOGRAPHY.label,
  },
});
